import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { distinctUntilChanged } from 'rxjs/operators';
import { combineLatest, map, Observable, Subscription, take } from 'rxjs';

import {
  DocumentSortField,
  DocumentStatus,
  ISharedDocumentsResponse,
  ISharedFolderContentResponse,
  ISort,
  LoadingType,
} from '../../models';
import { IActionState } from '../../utils';
import { CustomerService, FileDownloadService, RouterService } from '../../services';
import {
  getDocumentSorting,
  getResidentAppSettings,
  getSelectedContract,
  GetSharedContent,
  getSharedContent,
  getSharedContentActionState,
  GetUnread,
  IMasterDataState,
  ISharedContentState,
  ISortingState,
} from '../../+state';
import { A11yService } from '../../infrastructure';

@UntilDestroy()
@Directive()
export class SharedContentWidgetBaseComponent implements OnInit, OnDestroy {
  @Input() title = 'shared_content_header_l';
  @Input() documentsActionState: IActionState;
  @Input() id: string;
  @Output() viewAttachment = new EventEmitter();
  public sharedContent$: Observable<ISharedFolderContentResponse>;
  public sharedContentActionState$: Observable<IActionState>;
  public documentDownloadActionState$: Observable<IActionState>;
  public documentSortSupported$: Observable<boolean>;
  public header = true;
  public errorScreenUrl = '/content/home/document-error-screen';
  public loadingTypes: LoadingType[] = [];
  public onlyExpiredContractsAndActiveMembership$: Observable<boolean> = this.customerService
    .onlyExpiredContractsAndActiveMembership()
    .pipe(take(1));
  private contractId: string;
  private selectedSort: ISort<DocumentSortField>;
  private documentSub: Subscription;

  constructor(
    protected store: Store<ISharedContentState | IMasterDataState | ISortingState>,
    private routerService: RouterService,
    private fileDownloadService: FileDownloadService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private customerService: CustomerService,
    public a11yService: A11yService
  ) {}

  ngOnInit() {
    if (this.id) {
      this.header = false;
    }
    this.documentSub = combineLatest([
      this.store.select(getSelectedContract),
      this.store.select(getDocumentSorting),
    ])
      .pipe(distinctUntilChanged())
      .subscribe(([selectedContract, selectedSort]) => {
        this.contractId = selectedContract?.id;
        this.selectedSort = selectedSort;
        this.getSharedContent();
      });
    this.documentSortSupported$ = this.store
      .select(getResidentAppSettings)
      .pipe(map(settings => settings?.documentSortingSupported));
    this.sharedContent$ = this.store.select(getSharedContent);
    this.sharedContentActionState$ = this.store.select(getSharedContentActionState);
    this.documentDownloadActionState$ = this.fileDownloadService.documentDownloadActionState$;
  }

  ngOnDestroy() {
    this.documentSub?.unsubscribe();
  }

  public openFolder(id: string) {
    this.routerService
      .navigate(['/content'], { skipLocationChange: true })
      .then(() => this.routerService.navigate(['/content/home/shared-content-page', id]));
  }

  public openDocument(document: ISharedDocumentsResponse) {
    this.fileDownloadService
      .downloadDocument(document.id)
      .pipe(take(1))
      .subscribe(
        (result: { file: File; header: DocumentStatus }) => {
          if (!result.file) {
            this.routerService.navigate([
              '/content/home/shared-content-wait-page/' + result.header,
            ]);
          } else {
            this.store.dispatch(GetUnread());
            this.routerService.navigate([
              '/content/home/shared-content-details-page',
              {
                downloadDocument: JSON.stringify(result.file),
                documentName: document?.name,
              },
            ]);
          }
        },
        () => {
          void this.routerService.navigate(
            [this.errorScreenUrl, { id: this.id, url: this.router.url }],
            {
              relativeTo: this.activeRoute,
              skipLocationChange: true,
            }
          );
        }
      );
  }

  protected getSharedContent(forced?: boolean) {
    this.store.dispatch(
      GetSharedContent({
        params: {
          folderId: this.id || null,
          contractId: this.contractId || null,
          sortOrder: this.selectedSort?.direction || null,
          sortField: this.selectedSort?.value || null,
        },
        forced: forced ? forced : false,
      })
    );
  }
}
